import React, { useEffect, useState } from 'react'
import {DollarSign, Briefcase, Star, TrendingUp, CheckCircle, Clock} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface DashboardProps {
  userId?: string
}

export function Dashboard({ userId, onViewProfile }: DashboardProps) {
  const [stats, setStats] = useState({
    totalTasks: 0,
    activeTasks: 0,
    completedTasks: 0,
    totalSpent: 0,
    totalEarned: 0,
    avgRating: 0
  })
  const [myTasks, setMyTasks] = useState<any[]>([])
  const [myAgents, setMyAgents] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (userId) {
      fetchDashboardData()
    }
  }, [userId])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // Fetch tasks posted by user
      const { list: postedTasks } = await lumi.entities.tasks.list({
        filter: { requesterId: userId },
        sort: { createdAt: -1 }
      })

      // Fetch tasks claimed by user (as agent)
      const { list: claimedTasks } = await lumi.entities.tasks.list({
        filter: { agentId: userId },
        sort: { createdAt: -1 }
      })

      // Fetch user's agents
      const { list: agents } = await lumi.entities.agents.list({
        filter: { creator: userId },
        sort: { createdAt: -1 }
      })

      const allTasks = [...postedTasks, ...claimedTasks]
      const completedTasks = allTasks.filter(t => t.status === 'completed')
      const activeTasks = allTasks.filter(t => ['claimed', 'in_progress', 'submitted'].includes(t.status))

      const totalSpent = postedTasks.reduce((sum, t) => sum + (t.budget || 0), 0)
      const totalEarned = agents.reduce((sum, a) => sum + (a.totalEarnings || 0), 0)
      const avgRating = agents.length > 0
        ? agents.reduce((sum, a) => sum + (a.rating || 0), 0) / agents.length
        : 0

      setStats({
        totalTasks: allTasks.length,
        activeTasks: activeTasks.length,
        completedTasks: completedTasks.length,
        totalSpent,
        totalEarned,
        avgRating
      })

      setMyTasks(allTasks.slice(0, 5))
      setMyAgents(agents)
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!userId) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-400 text-lg">Please login to view your dashboard</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Dashboard</h2>
        <p className="text-slate-400">Overview of your activity</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border border-purple-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <Briefcase className="w-8 h-8 text-purple-400" />
            <span className="text-3xl font-bold text-white">{stats.totalTasks}</span>
          </div>
          <p className="text-slate-300 font-medium">Total Tasks</p>
          <p className="text-sm text-slate-400 mt-1">{stats.activeTasks} active</p>
        </div>

        <div className="bg-gradient-to-br from-purple-400/20 to-purple-600/20 border border-purple-400/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-purple-300" />
            <span className="text-3xl font-bold text-white">{stats.completedTasks}</span>
          </div>
          <p className="text-slate-300 font-medium">Completed</p>
          <p className="text-sm text-slate-400 mt-1">Success rate: {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <DollarSign className="w-8 h-8 text-purple-300" />
            <span className="text-3xl font-bold text-white">${stats.totalSpent.toFixed(0)}</span>
          </div>
          <p className="text-slate-300 font-medium">Total Spent</p>
          <p className="text-sm text-slate-400 mt-1">On task bounties</p>
        </div>

        <div className="bg-gradient-to-br from-purple-300/20 to-purple-500/20 border border-purple-300/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp className="w-8 h-8 text-purple-200" />
            <span className="text-3xl font-bold text-white">${stats.totalEarned.toFixed(0)}</span>
          </div>
          <p className="text-slate-300 font-medium">Total Earned</p>
          <p className="text-sm text-slate-400 mt-1">As agent</p>
        </div>

        <div className="bg-gradient-to-br from-purple-700/20 to-purple-900/20 border border-purple-700/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <Star className="w-8 h-8 text-purple-400" />
            <span className="text-3xl font-bold text-white">{stats.avgRating.toFixed(1)}</span>
          </div>
          <p className="text-slate-300 font-medium">Avg Rating</p>
          <p className="text-sm text-slate-400 mt-1">{myAgents.length} agent(s)</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800/20 to-black/20 border border-gray-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <Clock className="w-8 h-8 text-gray-400" />
            <span className="text-3xl font-bold text-white">{stats.activeTasks}</span>
          </div>
          <p className="text-slate-300 font-medium">In Progress</p>
          <p className="text-sm text-slate-400 mt-1">Active now</p>
        </div>
      </div>

      {myAgents.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">My Agents</h3>
          <div className="grid gap-4">
            {myAgents.map((agent) => (
              <div
                key={agent._id}
                className="bg-black/50 border border-gray-800 rounded-xl p-6 hover:border-purple-500/50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-xl font-bold text-white mb-1">{agent.name}</h4>
                    <p className="text-slate-400 text-sm mb-3">{agent.category}</p>
                    <div className="flex gap-4">
                      <div className="text-sm">
                        <span className="text-slate-500">Tasks: </span>
                        <span className="text-white font-semibold">{agent.tasksCompleted}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">Earned: </span>
                        <span className="text-purple-400 font-semibold">${agent.totalEarnings}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-500">Rating: </span>
                        <span className="text-yellow-400 font-semibold">{agent.rating}/5</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    agent.availability === 'online' ? 'bg-purple-500/20 text-purple-400' :
                    agent.availability === 'busy' ? 'bg-gray-500/20 text-gray-400' :
                    'bg-gray-700/20 text-gray-500'
                  }`}>
                    {agent.availability.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {myTasks.length > 0 && (
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Recent Tasks</h3>
          <div className="grid gap-4">
            {myTasks.map((task) => (
              <div
                key={task._id}
                className="bg-black/50 border border-gray-800 rounded-xl p-6 hover:border-purple-500/50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-1">{task.title}</h4>
                    <p className="text-slate-400 text-sm line-clamp-1">{task.description}</p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <span className="text-lg font-bold text-white">${task.budget}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      task.status === 'completed' ? 'bg-purple-500/20 text-purple-400' :
                      task.status === 'claimed' ? 'bg-purple-300/20 text-purple-300' :
                      'bg-purple-700/20 text-purple-500'
                    }`}>
                      {task.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
